import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { refreshSession } from '@/api/authSession';
import { getBaseUrl } from '@/api/config';
import { captureMessage } from '@/analytics/errorReporting';
import { doNothing } from '@/utils/doNothing';
// Straight from Tasks.api, not the `@/pages/tasks` barrel: this hook runs app-wide, and the barrel
// would pull the lazy-loaded Tasks page into the main bundle.
import { TASKS_QUERY_KEY } from '@/pages/tasks/Tasks.api';

/**
 * Maps each streamed resource (an SSE event name emitted by the API's `/stream`
 * endpoint, named after the changed table) to the query keys that should be
 * invalidated when it changes.
 *
 * To add realtime for a new resource: add an entry here, add the table to the
 * API's REALTIME_TABLES + a migration. Nothing else — it rides the same single
 * connection.
 */
const STREAM_INVALIDATIONS: Record<string, QueryKey[]> = {
  tasks: [TASKS_QUERY_KEY, ['top-priorities']],
};

/**
 * Reconnect backoff. A CLOSED `EventSource` never retries on its own (the
 * browser gives up on a mobile background/network switch or a server timeout),
 * so we reconnect manually with capped exponential backoff.
 */
const RECONNECT_BASE_DELAY_MS = 1_000;
const RECONNECT_MAX_DELAY_MS = 30_000;
const CLOSES_BEFORE_REPORT = 5;
// `EventSource` never exposes the HTTP status, so an expired access token looks like any other
// close. Every this-many consecutive closes, a token-authenticated stream refreshes the session
// (single flight, shared with apiFetch) before reconnecting, instead of replaying a dead token.
export const CLOSES_BEFORE_REFRESH = 3;

/**
 * Opens a SINGLE Server-Sent Events connection to the API and invalidates the
 * matching TanStack queries whenever the backend reports a change, so the UI
 * re-fetches automatically. Recovers from connection drops on its own.
 */
type AccessTokenProvider = () => Promise<string | null>;

const streamUrl = async (getAccessToken?: AccessTokenProvider): Promise<string> => {
  const url = `${getBaseUrl()}/stream`;
  const token = getAccessToken ? await getAccessToken() : null;
  return token ? `${url}?access_token=${encodeURIComponent(token)}` : url;
};

/**
 * Native Khova sessions pass `getAccessToken`: `EventSource` cannot send a bearer header, so the
 * API accepts the access token as a query parameter on `/stream` only. Each reconnect reads the
 * current token, so a refreshed session keeps streaming.
 */
export const useRealtimeStream = (enabled: boolean, getAccessToken?: AccessTokenProvider): void => {
  const queryClient = useQueryClient();
  const sourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const consecutiveClosesRef = useRef(0);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const invalidate = (keys: QueryKey[]) => {
      for (const queryKey of keys) {
        queryClient.invalidateQueries({ queryKey }).catch(doNothing);
      }
    };

    let isActive = true;

    const connect = async () => {
      sourceRef.current?.close();

      const url = await streamUrl(getAccessToken);
      if (!isActive) {
        return;
      }

      const source = new EventSource(url, {
        withCredentials: true,
      });
      sourceRef.current = source;

      source.onopen = () => {
        consecutiveClosesRef.current = 0;
        invalidate(Object.values(STREAM_INVALIDATIONS).flat());
      };

      source.onerror = () => {
        // A non-CLOSED state is a transient drop the browser retries on its own.
        if (source.readyState !== EventSource.CLOSED) {
          return;
        }

        source.onerror = null;
        source.close();
        consecutiveClosesRef.current += 1;
        const closes = consecutiveClosesRef.current;

        if (closes === CLOSES_BEFORE_REPORT) {
          captureMessage('Realtime stream failed to reconnect after repeated closes');
        }

        const shouldRefresh = Boolean(getAccessToken) && closes % CLOSES_BEFORE_REFRESH === 0;
        const delay = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** (closes - 1), RECONNECT_MAX_DELAY_MS);
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = setTimeout(() => reconnect(shouldRefresh).catch(doNothing), delay);
      };

      for (const [resource, keys] of Object.entries(STREAM_INVALIDATIONS)) {
        source.addEventListener(resource, () => invalidate(keys));
      }
    };

    const reconnect = async (shouldRefresh: boolean) => {
      if (shouldRefresh) {
        await refreshSession();
      }
      if (isActive) {
        await connect();
      }
    };

    connect().catch(doNothing);

    return () => {
      isActive = false;
      clearTimeout(reconnectTimerRef.current);
      sourceRef.current?.close();
      consecutiveClosesRef.current = 0;
    };
  }, [enabled, getAccessToken, queryClient]);
};
